import { Context as DiceWorkerContext } from "@/context";
import { useUserDiceData } from "@/hooks/useUserDiceData";
import type { User } from "@dndbeyond/auth/types/user";
import { isEmpty } from "lodash";
import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { getPhysicsWorker } from "./helpers/getPhysicsWorker";
import { getRenderedWorker } from "./helpers/getRenderedWorker";
import { useAudioStartListener } from "./hooks/useAudioStartListener";
import { useUserManifest } from "./hooks/useUserManifest";

const DiceCanvases = lazy(async () =>
  import("./DiceCanvases").then((module) => ({ default: module.DiceCanvases })),
);

interface PddContextProviderProps extends PropsWithChildren {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- logger type needs to be properly decoupled from feywild
  logger: any;
  errorCallback?: (message: string) => void;
  nodeEnv: string;
  env: string;
  user?: User;
  gameId?: string;
  toastCallback?: (message: string) => void;
  preview?: boolean;
  canUseShared3dDice?: boolean;
}

export const PddContextProvider: FC<PddContextProviderProps> = ({
  children,
  logger,
  errorCallback,
  nodeEnv,
  env,
  user,
  gameId,
  toastCallback,
  preview,
  canUseShared3dDice,
}) => {
  const hasWorkersInitialized = useRef(false);

  const [physicsWorker, setPhysicsWorker] = useState<Worker>();
  const [hasPhysicsWorkerErrored, setHasPhysicsWorkerErrored] = useState(false);
  const [isPhysicsWorkerReady, setIsPhysicsWorkerReady] = useState(false);

  const [renderedWorker, setRenderedWorker] = useState<Worker>();
  const [hasRenderedWorkerErrored, setHasRenderedWorkerErrored] =
    useState(false);
  const [isRenderedWorkerReady, setIsRenderedWorkerReady] = useState(false);

  const hasPddErrored = hasPhysicsWorkerErrored || hasRenderedWorkerErrored;
  const isPddReady = isPhysicsWorkerReady && isRenderedWorkerReady;

  useUserDiceData(nodeEnv, env, Number(user?.id));
  useAudioStartListener(canUseShared3dDice, user);

  const userManifest = useUserManifest(
    nodeEnv,
    env,
    user,
    preview,
    canUseShared3dDice,
  );

  useEffect(() => {
    if (
      !Boolean(canUseShared3dDice) ||
      !user ||
      isEmpty(userManifest) ||
      hasWorkersInitialized.current
    )
      return;

    (async () => {
      setPhysicsWorker(await getPhysicsWorker(nodeEnv, env, user));
      setRenderedWorker(await getRenderedWorker(nodeEnv, env, user));

      hasWorkersInitialized.current = true;
    })();
  }, [canUseShared3dDice, env, userManifest, nodeEnv, user]);

  // pass-through children if no user is present
  // e.g. readonly character sheets (can't roll dice)
  if (!(Boolean(canUseShared3dDice) && user)) return children;

  return (
    <DiceWorkerContext.Provider
      value={{
        renderedWorker,
        physicsWorker,
        nodeEnv,
        env,
        user,
        hasPddErrored,
        isPddReady,
        userManifest,
        setHasPhysicsWorkerErrored,
        setIsPhysicsWorkerReady,
        gameId,
        logger,
        errorCallback,
        toastCallback,
        setHasRenderedWorkerErrored,
        setIsRenderedWorkerReady,
      }}
    >
      {children}
      <Suspense>
        <DiceCanvases />
      </Suspense>
    </DiceWorkerContext.Provider>
  );
};
